/**
 * Reads the background color painted at the top edge of the screen.
 * The native status bar sits above the web view, so it should show the same color.
 * @param {Document} doc Document to sample.
 * @returns {string | null} Hex color like `#f7f7f8`, or null when nothing opaque is painted.
 */
export function readScreenBackground(doc: Document = document): string | null {
  const view = doc.defaultView

  if (!view) {
    return null
  }

  let element: Element | null = doc.elementFromPoint(Math.floor(view.innerWidth / 2), 1)

  while (element) {
    const hex = toHexColor(view.getComputedStyle(element).backgroundColor)

    if (hex) {
      return hex
    }

    element = element.parentElement
  }

  return toHexColor(view.getComputedStyle(doc.body).backgroundColor)
}

/**
 * Converts an opaque `rgb()` or `rgba()` color to a hex string.
 * @param {string} color Computed CSS color.
 * @returns {string | null} Hex color, or null for transparent or unknown values.
 */
export function toHexColor(color: string): string | null {
  const match = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/.exec(color.trim())

  if (!match) {
    return null
  }

  const [, red, green, blue, alpha] = match

  if (alpha !== undefined && Number(alpha) < 1) {
    return null
  }

  return `#${[red, green, blue].map(part => Number(part).toString(16).padStart(2, '0')).join('')}`
}
